/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

/* eslint-disable no-invalid-this */

'use strict';

// MODULES //

import IS_LITTLE_ENDIAN from '@stdlib/assert-is-little-endian';
import isRealFloatingPointDataType from '@stdlib/array-base-assert-is-real-floating-point-data-type'; // eslint-disable-line id-length
import isSignedIntegerDataType from '@stdlib/array-base-assert-is-signed-integer-data-type';
import { isPrimitive as isNumber } from '@stdlib/assert-is-number';
import { isPrimitive as isBoolean } from '@stdlib/assert-is-boolean';
import { isPrimitive as isInteger } from '@stdlib/assert-is-integer';
import { isPrimitive as isBigInt } from '@stdlib/assert-is-bigint';
import isComplexLike from '@stdlib/assert-is-complex-like';
import isAllowedCast from '@stdlib/ndarray-base-assert-is-allowed-data-type-cast';
import minDataType from '@stdlib/array-min-dtype';
import minSignedIntegerDataType from '@stdlib/array-base-min-signed-integer-dtype';
import complexDType from '@stdlib/complex-dtype';
import format from '@stdlib/error-tools-fmtprodmsg';
import boolean2number from './boolean2number.js';
import bigint2number from './bigint2number.js';
import PRIVATE_BUFFER from './private_buffer.js';
import defaults from './defaults.json';


// MAIN //

/**
* Returns a function for writing field data.
*
* @private
* @param {Object} obj - field object
* @param {string} method - data view method name
* @returns {Function} function for writing field data
*/
function setNumber( obj, method ) {
	return setter;

	/**
	* Writes a number value to an underlying byte buffer.
	*
	* @private
	* @param {*} value - value to set
	* @throws {TypeError} cannot cast provided values to field data type
	*/
	function setter( value ) {
		var view;
		var dt;
		var v;
		if ( isNumber( value ) ) {
			if ( isRealFloatingPointDataType( obj.type ) ) {
				dt = obj.type;
			} else if ( !isInteger( value ) ) {
				dt = defaults.dtypes.real;
			} else if ( isSignedIntegerDataType( obj.type ) ) {
				dt = minSignedIntegerDataType( value );
			} else {
				dt = minDataType( value );
			}
			v = value;
		} else if ( isComplexLike( value ) ) {
			dt = complexDType( value ) || ( ( obj.type === 'float32' ) ? 'complex64' : 'complex128' );
			v = value.re; // discard imaginary component
		} else if ( isBoolean( value ) ) {
			dt = 'bool';
			v = boolean2number( value );
		} else if ( isBigInt( value ) ) {
			dt = 'int64'; // note: the specific integer data type (int64 vs uint64) should not matter here
			v = bigint2number( value );
		} else {
			dt = 'generic';
			v = value;
		}
		if ( !isAllowedCast( dt, obj.type, obj.castingMode ) ) {
			throw new TypeError( format( 'invalid assignment. Assigned value cannot be cast to the data type of `%s`. Data types: [%s, %s].', obj.name, obj.type, dt ) );
		}
		view = this[ PRIVATE_BUFFER ];
		view[ method ]( obj.byteOffset, v, IS_LITTLE_ENDIAN );
	}
}


// EXPORTS //

export default setNumber;
